/**
 * SkyModule (Atlas) — Phase B Hillaire procedural sky swap.
 *
 * Wraps the EngineX `SkyModule` (4-LUT Hillaire on WebGPU, inline ray-march
 * on WebGL2 via `pipeline: 'auto'`) and wires it to Atlas's per-world
 * `skyState.timeOfDay` clock.
 *
 * What this module owns:
 *   - Instantiates + inits the engine sky (which sets `scene.backgroundNode`,
 *     sets `scene.background = null`, and starts the PMREM-IBL bake cycle).
 *   - After the engine init, replaces `scene.backgroundNode` with a composite:
 *     atmosphere + a PHOTOGRAPHIC night sky (8K Higgsfield astrophotography
 *     equirect from /sky/, world-static, gated by `uNightFactor`).  The former
 *     procedural starfield / milky way / nebula / volcanic-planet layers were
 *     removed — the photo IS the night sky.  The composite is re-assigned AFTER
 *     `engine.init(app)` so it wraps the SAME memoized node the engine bake uses.
 *   - Exports module-level `uSunDir` and `uNightFactor` uniforms that other
 *     modules (weather/clouds) import and read from their own TSL graphs, and
 *     `currentTod()` — the override-aware clock LightingModule dims by.
 *   - A RenderSub/priority-5 system updates the uniforms each frame from the
 *     same time-of-day source as the engine-sky `sunDirectionProvider`.
 *
 * CRITICAL (r182 rule): ALL TSL primitives (`uniform`, `vec3`, node `.add`/
 * `.mul`) are imported from `three/tsl`, NOT off the `three/webgpu` namespace.
 * `(THREE as any).foo` is `undefined` at runtime and breaks `vite build`.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { SkyModule as EngineSky } from 'enginex/render/sky/SkyModule.js';
import { uniform, vec3, positionWorldDirection, texture as tslTexture, equirectUV } from 'three/tsl';
import { Phase, Sub, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { sunDirection, nightFactor, stepTod, flatSkyFallbackColor } from '../lib/lighting.js';
import { buildNightLive, airmassWeight } from '../lib/sky/night_live.js';
import { buildStarField, type StarFieldInput } from '../lib/sky/star_field.js';
import { parseStarCatalog, bucketStars, STAR_GRID } from '../lib/sky/star_catalog.js';
import { stationAt, ambientSatsAt, meteorAt, battleSkyAt, type Vec3 } from '../lib/night_sky_schedule.js';
import { Texture, TextureLoader, SRGBColorSpace, DataTexture, RGBAFormat, FloatType, NearestFilter } from 'three/webgpu';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { activePreset } from '../lib/quality_tiers.js';
import type { DbConnection } from '../module_bindings/index.js';

// ── Shared uniforms (other modules import these) ──────────────────────────────
/** World-space unit vector FROM origin TO the sun (updated every RenderSub frame). */
export const uSunDir = uniform(vec3(0, 1, 0));
/** 0 = full day, 1 = full night.  Gates the photographic night-sky layer. */
export const uNightFactor = uniform(0);

// ── Living night sky (2026-08-27): uniform slots the CPU scheduler drives ────
// (lib/night_sky_schedule.ts computes WHERE everything is from the shared wall
// clock; lib/sky/night_live.ts draws it. See those headers.)
const uMovers = [0, 1, 2].map(() => ({ dir: uniform(vec3(0, -1, 0)), bright: uniform(0) }));
const uMeteor = { dir: uniform(vec3(0, -1, 0)), vel: uniform(vec3(1, 0, 0)), len: uniform(0), bright: uniform(0) };
const uBattle = { dir: uniform(vec3(0, -1, 0)), tangent: uniform(vec3(1, 0, 0)), active: uniform(0) };
/** Live-layer master gain (dev/probe tuning + the pixel-diff proof harness). */
const uLiveGain = uniform(1);
/** Starless-background gain (the old photo ran at 1.15) and the point-star gain — probe A/B levers. */
const uBgGain = uniform(1.15);
const uStarGain = uniform(1.15); // the same gain as the background: the catalogue's flux is the photo's
/** What the star catalogue became on this client (`__nightStars()`). */
let nightStarsState: Record<string, unknown> = { count: 0, entries: 0, error: null };
const setV3 = (un: { value: unknown }, v: Vec3): void =>
  (un.value as { set: (x: number, y: number, z: number) => void }).set(v.x, v.y, v.z);
/** Probe mirror (__nightSky) — the last schedule the uniforms were driven to. */
const nightSkyState = { nightFactor: 0, station: 0, sats: [0, 0], satY: [0, 0], meteor: 0, battle: 0, battleDirY: 0 };

// ── Local time-of-day override (dev/admin) ────────────────────────────────────
/**
 * When non-null, `currentWorldTimeOfDay()` returns this instead of the live
 * `skyState` clock — a VIEW-ONLY override (the admin debug menu's Local mode +
 * the preview keys). It drives the sun, clouds, and the night-sky gates in this
 * client only; it never mutates shared state. null = follow the live clock.
 */
let debugTodOverride: number | null = null;
if (typeof window !== 'undefined') (window as unknown as Record<string, unknown>).__setTod = (v: number | null): void => setDebugTod(v);
/** Debug menu (Local mode) / preview keys: set the view-only time-of-day. null = clear → live clock. */
export function setDebugTod(v: number | null): void {
  debugTodOverride = v;
  // Probe lever (headless screenshots want daylight): `__setTod(0.5)` / `__setTod(null)`.
  (window as unknown as Record<string, unknown>).__setTod = setDebugTod;
  // Fog reads the tod band event-driven (AtmosphereModule.applyFromSky), so a
  // preview jump must ping it — otherwise `\` shows a midnight sky with noon fog.
  bus.emit('atlas:environment-updated', {});
}

// ── Per-world clock helper ────────────────────────────────────────────────────
/**
 * Returns the time-of-day (0..1) for the CURRENT world the local avatar is in.
 *
 * Priority:
 *   0. `debugTodOverride` (dev/admin Local override) when set — wins over all.
 *   1. `atlas.currentWorldId` → `skyState.worldId.find(wid)?.timeOfDay`
 *   2. commons world's `skyState` (mirror of `LightingModule.commonsTimeOfDay`)
 *   3. 0.5 (midday fallback when the DB is not yet connected)
 */
function currentWorldTimeOfDay(): number {
  if (debugTodOverride !== null) return debugTodOverride; // dev/admin Local time override (debug menu + preview keys)
  const conn: DbConnection | null = atlas.conn;
  if (!conn) return 0.5;

  // 1. Current world the player is in.
  const wid = atlas.currentWorldId;
  if (wid != null) {
    const s = conn.db.skyState.worldId.find(wid);
    if (s) return s.timeOfDay;
  }

  // 2. Commons world fallback — O(1) via the resolved id (bug sweep: this
  // walked the ENTIRE world table, one row per personal world, three-plus
  // times per frame whenever the current world had no sky row). When the id
  // is not resolved yet (boot, synthetic harnesses), the old iteration is
  // the correctness fallback — rare, so the per-frame cost stays gone.
  if (atlas.commonsWorldId !== null) {
    const s = conn.db.skyState.worldId.find(atlas.commonsWorldId);
    if (s) return s.timeOfDay;
  } else {
    for (const w of conn.db.world.iter()) {
      if (w.kind === 'commons') {
        const s = conn.db.skyState.worldId.find(w.worldId);
        if (s) return s.timeOfDay;
      }
    }
  }

  return 0.5;
}

/**
 * The time-of-day the sky is rendering RIGHT NOW (live world clock or the
 * debug/preview override).  LightingModule reads this every frame so the sun
 * arc + ambient level always match the VISIBLE sky — previewing midnight via
 * the `\` key dims the world exactly like real midnight.
 */
export function currentTod(): number { return currentWorldTimeOfDay(); }

// ── Atlas SkyModule ───────────────────────────────────────────────────────────
export class SkyModule implements IModule {
  /** Distinct name — the engine SkyModule uses 'Sky'. */
  readonly name = 'AtlasSky';

  private _engineSky: EngineSky | null = null;
  /** Boot loading screen: true once the night photo reached a terminal state
   *  (KTX2 loaded, webp fallback loaded, or both failed → atmosphere-only). */
  private _photoResolved = false;
  get ready(): boolean { return this._flatReady || (this._engineSky !== null && this._photoResolved); }
  /**
   * Stored keydown handler for cleanup in dispose().
   * Added at init time so the night sky is instantly inspectable via dev keys.
   */
  private _keyHandler: ((e: KeyboardEvent) => void) | null = null;

  /** MINIMAL sky (2026-09-08): a flat background the colour of the fog. */
  private flat: THREE.Color | null = null;
  private _flatReady = false;
  private _flatSource: 'atmosphere' | 'scene.fog' | 'fallback' = 'fallback';

  /**
   * The flat sky of the minimal tier: no atmosphere, no LUTs, no IBL bake, no
   * night photo, no living sky — `scene.background` is a colour copied from
   * the fog every frame, so the fog wall and the sky are one surface. uSunDir
   * and the night factor still track the hour (water, lights and fog read them).
   */
  private initFlat(app: App): void {
    const scene = app.world.scene as unknown as { background: unknown; backgroundNode?: unknown; fog?: { color?: THREE.Color } | null };
    this.flat = new THREE.Color(0x8fa8c0);
    scene.backgroundNode = null;
    scene.background = this.flat;
    this._photoResolved = true;
    this._flatReady = true;
    app.world.addSystem(makeSystem('AtlasSkyUniforms', Phase.RenderSub, 5, () => {
      const d = sunDirection(currentWorldTimeOfDay());
      (uSunDir.value as { set: (x: number, y: number, z: number) => void }).set(d.x, d.y, d.z);
      const nf = nightFactor(d.y);
      (uNightFactor as unknown as { value: number }).value = nf;
      nightSkyState.nightFactor = nf;
      // THE ALWAYS-BLUE SKY (2026-09-09, "the sky is always blue … it is
      // midnight"): this copied `scene.fog.color`, so whenever the fog was
      // detached from the scene (the probe light rig, space, a personal
      // preset's restore, a world script's fog swap) the colour froze at the
      // boot blue for the rest of the session. Copy the ATMOSPHERE MODULE's
      // own fog colour instead — it tracks the visible time-of-day band and
      // the weather whether or not the scene carries it — and fall back to a
      // pure day/night ramp when the module is absent; re-assert the
      // background object each tick so a restore path can never swap it out.
      const atmo = app.getModule('Atmosphere') as { fogColor?: THREE.Color } | undefined;
      const src = atmo?.fogColor ?? scene.fog?.color ?? null;
      if (this.flat) {
        if (src) this.flat.copy(src); else flatSkyFallbackColor(nf, this.flat);
        if (!scene.backgroundNode && scene.background !== this.flat) scene.background = this.flat;
      }
      this._flatSource = src ? (atmo?.fogColor ? 'atmosphere' : 'scene.fog') : 'fallback';
    }, Sub.Update));
    (globalThis as unknown as { __sky?: () => unknown }).__sky = () => ({ mode: 'flat', color: this.flat ? `#${this.flat.getHexString()}` : null, source: this._flatSource, night: nightSkyState.nightFactor });
  }

  init(app: App): void {
    if (activePreset().skyQuality === 'flat') { this.initFlat(app); return; }
    (globalThis as unknown as { __sky?: () => unknown }).__sky = () => ({ mode: 'full' });
    // Build and register the engine SkyModule.  It will:
    //   • create SkyRenderer + set scene.backgroundNode (memoized)
    //   • set scene.background = null
    //   • create SkyEnvironmentBaker
    //   • register SkyRuntimeSystem at RenderSub/priority 6 (calls
    //     sunDirectionProvider each frame + re-bakes PMREM → scene.environment)
    this._engineSky = new EngineSky({
      sunDirectionProvider: () => sunDirection(currentWorldTimeOfDay()),
      pipeline: 'auto',           // 4-LUT on WebGPU, inline ray-march on WebGL2
      // Aerial-perspective froxel bake is CPU-heavy per re-bake, so it only
      // runs when the active graphics tier wants the composite (PostModule
      // wires the volume into the post chain). Boot-time gate: a runtime tier
      // change picks it up on the next reload (the menu says so). LUT-path
      // only — on WebGL2's inline sky there is no LUT set and no volume.
      enableAerialPerspective: activePreset().aerialEnabled,
      // The engine default sunIlluminance (20) blew the sky to white AND over-lit the
      // world: this single value scales BOTH the visible sky radiance and the PMREM cube
      // baked into the IBL. 2.0 brings the sky back to a visible blue + a calm indirect
      // bounce; the direct sun DirectionalLight (2.8) + ambient are untouched so the lit
      // world keeps its punch (it can't go dark). In-browser tunable.
      sunIlluminance: 2.0,
      // A 2 h day cycle moves the sun ~0.07° in 5 s — re-baking the IBL every 5 s (the
      // default) was a periodic PMREM render-target realloc HITCH that read as "lag while
      // moving" (a frame stutter is invisible on a still image, visible in motion). 60 s
      // + the engine's own sun-drift re-bake keeps the IBL current with far fewer hitches.
      envBakeMaxInterval: 60,
      // Diffuse IBL needs no 128² cube; 64² quarters each bake's cost → a smaller hitch.
      envResolution: 64,
      // Ground-scale game on ±320 m relief: the real sky barely changes over a
      // few hundred meters, but tracking the camera into the LUT observer
      // height re-baked the sky-view LUT in DISCRETE steps as you climbed — a
      // visible sky "pop" per threshold plus CPU re-bakes mid-traversal.
      trackCameraAltitude: false,
    });
    this._engineSky.init(app);

    // World-space view ray for the photo night-sky layer.
    //
    // WHY `positionWorldDirection` (two bugs led here):
    //  1. "night sky shifts when you WALK": the background skybox is a UNIT sphere
    //     with an IDENTITY matrixWorld drawn camera-locked at the far plane (three
    //     `common/Background.js`, w=0 model-view), so `positionWorld` is a unit
    //     DIRECTION — the original `normalize(positionWorld - cameraPosition)`
    //     subtracted a metres-scale camera position from it and the star field
    //     parallaxed with translation.
    //  2. "night sky moves when you LOOK UP/DOWN": the interim fix reconstructed the
    //     ray from `screenUV` + `cameraProjectionMatrixInverse` + `cameraWorldMatrix`
    //     (w=0). But screenUV's vertical origin is a backend/context convention —
    //     in the WebGPU background-mesh context the reconstruction came out
    //     vertically flipped, so the sampled sky direction mis-tracked camera PITCH
    //     and the stars dragged with vertical look (yaw looked fine, which is why
    //     the flip survived review).
    //
    // `positionWorldDirection` is the background sphere's OWN per-fragment world
    // direction: translation-invariant by construction, correct under all camera
    // rotation on both backends (no screen-space conventions involved), and the
    // SAME ray the engine atmosphere uses since the SkyRenderer/skyViewNode fix —
    // the two layers stay aligned at every camera pose.
    const viewDir = positionWorldDirection;

    // Composite background node (linear HDR, upstream of bloom + the ACES tonemap):
    //   atmosphere = the memoized node the engine already assigned + bakes IBL from
    //   composite  = atmosphere + photoNight·nightFactor
    //
    // The night sky is the Higgsfield astrophoto SPLIT IN TWO (2026-09-11): a
    // starless 4K equirect for the Milky Way glow and a catalogue of its stars
    // drawn as points (see below). Both sample by WORLD direction
    // (`positionWorldDirection`, see above), so the sky is pinned to the WORLD —
    // turn or pitch your head and the constellations stay put; walk and they
    // never parallax (stars are at infinity).
    //
    // NOTE: do NOT .add(sunDisc) — the 4-LUT path (WebGPU) includes an analytic
    // sun disc inside buildBackgroundNode().  Adding a second disc would
    // double-draw the sun.
    const atmosphereNode = this._engineSky.renderer.buildBackgroundNode();
    // The night sky is TWO assets (2026-09-11, extract-sky-stars.mjs): a 4K
    // STARLESS background equirect (KTX2/UASTC first — PRE-FLIPPED vertically
    // like every sky KTX2, compressed textures can't honour flipY at upload —
    // with the WebP as the fallback) and a STAR CATALOGUE drawn as round
    // points on the sphere (lib/sky/star_field.ts): resolution-independent
    // and free of the equirect pole squeeze that turned the 8K photo's stars
    // into radial ellipses at the zenith. One background for every tier
    // (4 MB); the tier only sets the per-cell loop cap. The composite is
    // (re)assigned when both land — TSL binds the texture OBJECT at build
    // time; until then the background is atmosphere-only.
    const maxPerCell = activePreset().memoryBudgetMb >= 256 ? 64 : 32;
    const applySky = (bgTex: Texture | null, stars: StarFieldInput | null): void => {
      const bgRgb = bgTex ? tslTexture(bgTex, equirectUV(viewDir)).rgb : vec3(0);
      const starRgb = stars ? buildStarField(viewDir, stars, { airW: airmassWeight(viewDir), gain: uStarGain }) : vec3(0);
      const night = buildNightLive(viewDir, bgRgb, starRgb, {
        sunDir: uSunDir, gain: uLiveGain, bgGain: uBgGain, movers: uMovers, meteor: uMeteor, battle: uBattle,
      });
      // `atmosphereNode` IS `unknown` (engine type stub), so its `.add` needs the
      // cast; the Three.js NodeMaterial background path handles the node at runtime.
      (app.world.scene as unknown as { backgroundNode?: unknown }).backgroundNode =
        (atmosphereNode as { add: (n: unknown) => unknown }).add(night.mul(uNightFactor));
    };
    const loadBackground = new Promise<Texture | null>((resolve) => {
      const ktx = new KTX2Loader().setTranscoderPath('/basis/');
      ktx.detectSupport(app.renderer.native as never);
      ktx.load('/sky/night_sky_clear_bg.ktx2',
        (t) => { t.colorSpace = SRGBColorSpace; ktx.dispose(); resolve(t); },
        undefined,
        () => {
          ktx.dispose();
          new TextureLoader().load('/sky/night_sky_clear_bg.webp',
            (t) => { t.colorSpace = SRGBColorSpace; resolve(t); },
            undefined, () => resolve(null) /* both missing → atmosphere-only night IS the final state */);
        });
    });
    const loadStars: Promise<StarFieldInput | null> = fetch('/sky/night_sky_clear.stars.bin')
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((buf) => {
        const t0 = performance.now();
        const cat = parseStarCatalog(buf);
        const b = bucketStars(cat, STAR_GRID, maxPerCell);
        const mk = (data: Float32Array, w: number, h: number): DataTexture => {
          const t = new DataTexture(data, w, h, RGBAFormat, FloatType);
          t.minFilter = NearestFilter; t.magFilter = NearestFilter; t.generateMipmaps = false; t.flipY = false;
          t.needsUpdate = true;
          return t;
        };
        nightStarsState = {
          count: cat.count, entries: b.entries, occupied: b.occupied, maxInCell: b.maxInCell, capped: b.capped,
          grid: b.grid, maxPerCell, recordsHeight: b.recordsHeight, gpuBytes: (b.cells.length + b.records.length) * 4,
          bucketMs: Math.round(performance.now() - t0), error: null,
        };
        return { cells: mk(b.cells, b.cellsWidth, b.cellsHeight), records: mk(b.records, b.recordsWidth, b.recordsHeight), grid: b.grid, recordsWidth: b.recordsWidth, maxPerCell };
      })
      .catch((e: unknown) => { nightStarsState = { ...nightStarsState, error: String(e) }; console.warn('[AtlasSky] star catalogue unavailable:', e); return null; });
    Promise.all([loadBackground, loadStars]).then(([bg, stars]) => { applySky(bg, stars); this._photoResolved = true; });
    const composite = atmosphereNode;

    // The background node is now PURELY the sky (atmosphere + night layers).
    // Phase C originally composited the volumetric clouds here, but the
    // background skybox is a unit sphere drawn camera-locked at the far plane
    // (Three.js `Background.js` uses a w=0 model-view transform so it "will not
    // translate when the camera moves"), so finite-altitude clouds (1200–3500 m)
    // composited into it read as a band PINNED to the view. The clouds moved to
    // a depth-aware POST pass (PostModule → engine PostProcessingPipeline
    // `clouds` hook) where the cloud node ray-marches from the REAL camera
    // position: world-anchored, parallaxing, and occluded by opaque geometry via
    // the sky-pixel depth mask. See PostModule.ts.
    const sceneAny = app.world.scene as unknown as { backgroundNode?: unknown };
    sceneAny.backgroundNode = composite;
    // Probe: the living-sky schedule as the uniforms last saw it (headless
    // harnesses assert determinism + activity without reading pixels).
    (globalThis as Record<string, unknown>).__nightSky = () => ({ ...nightSkyState });
    (globalThis as Record<string, unknown>).__nightSkyGain = (v: number) => { (uLiveGain as unknown as { value: number }).value = v; return v; };
    (globalThis as Record<string, unknown>).__nightStars = () => ({ ...nightStarsState });
    (globalThis as Record<string, unknown>).__nightStarsGain = (v: number) => { (uStarGain as unknown as { value: number }).value = v; return v; };
    (globalThis as Record<string, unknown>).__nightBgGain = (v: number) => { (uBgGain as unknown as { value: number }).value = v; return v; };

    // RenderSub / priority 5 — runs BEFORE the engine bake at priority 6 so
    // the uniforms are current when the baker samples the sky-view LUT.
    app.world.addSystem(
      makeSystem(
        'AtlasSkyUniforms',
        Phase.RenderSub,
        5,
        (_dt, _world) => {
          const tod = currentWorldTimeOfDay();
          const d = sunDirection(tod);
          (uSunDir.value as { set: (x: number, y: number, z: number) => void })
            .set(d.x, d.y, d.z);
          const nf = nightFactor(d.y);
          (uNightFactor as unknown as { value: number }).value = nf;
          // ── The living sky: drive the traffic/battle uniforms from the
          // shared wall clock (a handful of hashes — negligible). Day skips
          // the traffic (invisible anyway) but zeroes the slots so a dusk
          // fade-in never pops a stale mover.
          nightSkyState.nightFactor = nf;
          if (nf > 0.001) {
            const nowS = Date.now() / 1000;
            const st = stationAt(nowS);
            const [sa, sb] = ambientSatsAt(nowS);
            setV3(uMovers[0].dir, st.dir); (uMovers[0].bright as { value: number }).value = st.bright;
            setV3(uMovers[1].dir, sa.dir); (uMovers[1].bright as { value: number }).value = sa.bright;
            setV3(uMovers[2].dir, sb.dir); (uMovers[2].bright as { value: number }).value = sb.bright;
            const me = meteorAt(nowS);
            setV3(uMeteor.dir, me.dir); setV3(uMeteor.vel, me.vel);
            (uMeteor.len as { value: number }).value = me.len;
            (uMeteor.bright as { value: number }).value = me.bright;
            const bs = battleSkyAt(nowS);
            setV3(uBattle.dir, bs.dir); setV3(uBattle.tangent, bs.tangent);
            (uBattle.active as { value: number }).value = bs.active;
            nightSkyState.station = st.bright; nightSkyState.sats = [sa.bright, sb.bright];
            nightSkyState.satY = [sa.dir.y, sb.dir.y]; // the MOVING quantity (probe aliveness)
            nightSkyState.meteor = me.bright; nightSkyState.battle = bs.active; nightSkyState.battleDirY = bs.dir.y;
          } else {
            (uMovers[0].bright as { value: number }).value = 0;
            (uMovers[1].bright as { value: number }).value = 0;
            (uMovers[2].bright as { value: number }).value = 0;
            (uMeteor.bright as { value: number }).value = 0;
            (uBattle.active as { value: number }).value = 0;
            nightSkyState.station = 0; nightSkyState.meteor = 0; nightSkyState.battle = 0;
          }
        },
        Sub.Update,
      ),
    );

    // ── Time-of-day preview keys (dev affordance) ────────────────────────────
    // WHY: The day/night cycle runs on a 2-hour real-time clock. Without a
    // shortcut the night sky is only visible after ~30 minutes of waiting. These
    // keys drive the LOCAL view-only time override (`setDebugTod`) so a developer
    // can instantly inspect the night-sky composite in-browser.
    //
    //   \  (Backslash) → jump to deep night  (timeOfDay = 0.0 ≈ midnight)
    //   [              → step tod −0.05       (move backward toward midnight)
    //   ]              → step tod +0.05       (move forward toward noon)
    //
    // tod = 0.5 is noon (sun at apex); tod ≈ 0.0 / 1.0 is midnight (sun below
    // horizon → nightFactor + deepNight → 1).  Setting tod = 0.0 is therefore
    // "jump to deep night" — the most useful single-key shortcut for reviewers.
    // The admin debug menu's Day/Night section shares this same Local override.
    //
    // Guard: skip when an input/textarea is focused (don't hijack typing).
    // Guard: skip auto-repeat (keydown fires repeatedly while held — one fire
    // per physical press is enough for a dev tool).
    //
    // These keys now mutate ONLY this client's view (no `debugSetSkyTime`, no
    // shared-Commons write — the prior griefing vector is gone). The dev-origin
    // gate below is retained to scope the keyboard affordance to developers, not
    // as a security boundary: register the listener ONLY on a local/dev origin,
    // or when a developer on a deployed build opts in via localStorage.
    const _hostname = (typeof location !== 'undefined' ? location.hostname : '') || '';
    const _localOrigin =
      _hostname === 'localhost' ||
      _hostname === '127.0.0.1' ||
      _hostname === '[::1]' ||
      _hostname === '';
    let _optIn = false;
    try {
      _optIn =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('atlas:skyPreview') === '1';
    } catch {
      /* storage blocked (private-browsing / permissions policy) */
    }
    const _previewEnabled = _localOrigin || _optIn;

    if (!_previewEnabled) {
      // Leave _keyHandler null — dispose() already null-guards the removeEventListener.
      console.info(
        '[AtlasSky] Time-preview keys disabled on non-local origin. ' +
          "To enable: localStorage.setItem('atlas:skyPreview','1') and reload.",
      );
    } else {
      this._keyHandler = (e: KeyboardEvent): void => {
        // Skip auto-repeat keydown events.
        if (e.repeat) return;
        // Skip when the user is typing in a text field.
        const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;

        // The keys now drive the Local view-only override (setDebugTod), NOT the
        // debugSetSkyTime reducer — so they no longer mutate the shared Commons
        // sky (the prior griefing vector); they need no connection or world id.
        //   \  → jump to deep night (tod 0.0)   [ → step −0.05   ] → step +0.05
        // currentWorldTimeOfDay() returns the override-or-live tod, so stepping
        // continues from wherever the view currently is.
        let newTod: number | null = null;
        if (e.key === '\\') newTod = 0.0;
        else if (e.key === ']') newTod = stepTod(currentWorldTimeOfDay(), 0.05);
        else if (e.key === '[') newTod = stepTod(currentWorldTimeOfDay(), -0.05);
        if (newTod === null) return;

        setDebugTod(newTod);
      };
      window.addEventListener('keydown', this._keyHandler);
    } // end if (_previewEnabled)
  }

  dispose(): void {
    // Remove the time-of-day preview key listener (Task 7).
    if (this._keyHandler) {
      window.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
    this._engineSky?.dispose();
    this._engineSky = null;
  }
}
